// ===========================================================================
// frontend/src/screens/CropListing/Congratulations.jsx
// NEW UI — Congratulations Popup/Modal
// Reference: Figma design — "Congratulations Your crop listing is now live."
// ===========================================================================

import React from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogContent,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    IconCirclePlus,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';

// ─── Constants ───────────────────────────────────────────────────────────────
const PRIMARY = colors.primary;
const PRIMARY_DARK = colors.primary_dark || '#149343';

const congratsTranslations = {
    en: {
        title: 'Congratulations',
        subtitle: 'Your crop listing is now live.',
        goDashboard: 'Go to Dashboard',
        createNew: 'Create New Listing',
    },
    hi: {
        title: 'बधाई हो',
        subtitle: 'आपकी फसल की सूची अब लाइव है।',
        goDashboard: 'डैशबोर्ड पर जाएं',
        createNew: 'नई लिस्टिंग बनाएं',
    },
    mr: {
        title: 'अभिनंदन',
        subtitle: 'तुमची पीक सूची आता लाइव्ह आहे.',
        goDashboard: 'डॅशबोर्डवर जा',
        createNew: 'नवीन नॅदी तयार करा',
    },
};

const Congratulations = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const langCode = i18n.language || 'en';
    const currentLang = langCode.startsWith('hi') ? 'hi' : (langCode.startsWith('mr') || langCode.startsWith('mar')) ? 'mr' : 'en';
    const tLocal = congratsTranslations[currentLang];

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleGoDashboard = () => {
        if (onClose) onClose();
        navigate('/home');
    };

    const handleCreateNew = () => {
        if (onClose) onClose();
        navigate('/add-crop-step-1');
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                // Disable closing on backdrop click or escape key to ensure navigation only via buttons
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                if (onClose) onClose();
            }}
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    padding: { xs: 2, md: 4 },
                    maxWidth: '440px',
                    width: '100%',
                    textAlign: 'center',
                }
            }}
        >
            <DialogContent>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                }}>
                    {/* Celebration Icon/Illustration Placeholder */}
                    <Box sx={{
                        width: 120,
                        height: 120,
                        backgroundColor: '#F4F6F8',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '60px',
                    }}>
                        🎉
                    </Box>

                    <Box>
                        <Typography sx={{
                            fontFamily: fonts.poppins,
                            fontWeight: 800,
                            fontSize: '28px',
                            color: '#1C252E',
                            mb: 1,
                        }}>
                            {tLocal.title}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: '16px',
                            color: '#637381',
                        }}>
                            {tLocal.subtitle}
                        </Typography>
                    </Box>

                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                    }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleGoDashboard}
                            sx={{
                                borderRadius: '50px',
                                py: 1.8,
                                backgroundColor: PRIMARY,
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '15px',
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: PRIMARY_DARK,
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {tLocal.goDashboard}
                        </Button>

                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={handleCreateNew}
                            startIcon={<IconCirclePlus size={20} />}
                            sx={{
                                borderRadius: '50px',
                                py: 1.8,
                                color: '#1C252E',
                                borderColor: '#E0E5EB',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '15px',
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: '#919EAB',
                                    backgroundColor: '#F4F6F8',
                                },
                            }}
                        >
                            {tLocal.createNew}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default Congratulations;
